import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";

import { adminGetRequestOverview } from "@/lib/admin.functions";
import { COUNTRY_BY_CODE } from "@/lib/countries";

export const Route = createFileRoute("/admin/")({
  staticData: { sitemap: false },
  component: AdminOverview,
});

const CARDS = [
  { to: "/admin/content/blog", title: "Blog", text: "Articles on planning, scheduling and MRP." },
  { to: "/admin/content/news", title: "News", text: "Releases, events and company updates." },
  { to: "/admin/content/case", title: "Case studies", text: "Customer stories and results." },
  { to: "/admin/content/industry", title: "Industries", text: "Industry landing pages." },
  {
    to: "/admin/content/integration",
    title: "Integrations",
    text: "Pages for connected systems: SAP, Dynamics 365, Power BI and more.",
  },
  { to: "/admin/requests", title: "Requests", text: "Demo, partnership and contact submissions." },
];

function AdminOverview() {
  const getRequestOverview = useServerFn(adminGetRequestOverview);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-request-overview"],
    queryFn: () => getRequestOverview({}),
  });
  const requests = data?.requests ?? [];

  return (
    <>
      <h1 className="font-display text-3xl font-bold">Content overview</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Write in English first, then translate into the other five languages with one click.
      </p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((card) => (
          <Link
            key={card.to}
            to={card.to as never}
            className="rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/50"
          >
            <h2 className="font-display text-lg font-semibold">{card.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{card.text}</p>
          </Link>
        ))}
      </div>

      <section className="mt-12" aria-labelledby="requests-heading">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-primary">{data?.total ?? 0} total</p>
            <h2 id="requests-heading" className="mt-1 font-display text-2xl font-bold">
              Requests
            </h2>
          </div>
          <Link to="/admin/requests" search={{ page: 1, pageSize: 25 }} className="text-sm font-medium text-primary hover:underline">
            View all requests
          </Link>
        </div>

        {isLoading ? (
          <div className="mt-8 flex justify-center py-8">
            <Loader2 className="size-6 animate-spin text-primary" aria-label="Loading requests" />
          </div>
        ) : requests.length === 0 ? (
          <p className="mt-6 text-sm text-muted-foreground">No requests yet.</p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-surface text-xs text-muted-foreground uppercase">
                <tr>
                  <th className="p-4">Name</th>
                  <th className="p-4">Email</th>
                  <th className="p-4">Country</th>
                  <th className="p-4">Industry</th>
                  <th className="p-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id} className="border-t border-border">
                    <td className="p-4 font-medium">{request.full_name}</td>
                    <td className="p-4">
                      <a href={`mailto:${request.email}`} className="text-muted-foreground hover:text-primary">
                        {request.email}
                      </a>
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {request.country ? (COUNTRY_BY_CODE[request.country] ?? request.country) : "—"}
                    </td>
                    <td className="p-4 text-muted-foreground">{request.industry ?? "—"}</td>
                    <td className="p-4 whitespace-nowrap text-muted-foreground">
                      {new Date(request.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
